/**
 * GMTEC-24197 – central org-code configuration.
 *
 * The root organisation "SE" is replaced by "O" (single root at all times; the change may be reverted, and historical
 * data keeps "SE"). Functional code never compares against a root org code: roots are detected from backend data
 * (`parentOrgCode === ROOT_PARENT_ORG_CODE`, `orgLevel === 0`, or – in Org Management – a missing `parentOrganisationId`).
 *
 * Business rules that are keyed on the root prefix use KNOWN_ROOT_ORG_CODES and work for every listed root.
 * Child org codes may keep a different root prefix during the transition (root "O" with children "SE GP"); rules
 * accept every known root. Mirrored backend rules are named below.
 */

export interface RootOrganisationCandidate {
  orgCode?: string | null;
  parentOrgCode?: string | null;
  orgLevel?: number | null;
}

/**
 * Sentinel the backend renders as `parentOrgCode` of level-0 organisations and accepts as `parentOrgCode` / `orgCode`
 * query value to request the top level. It is NOT an org code.
 */
export const ROOT_PARENT_ORG_CODE = 'root';

export function isRootParentOrgCode(parentOrgCode: string | null | undefined): boolean {
  return parentOrgCode === ROOT_PARENT_ORG_CODE;
}

/** Root detection for tree DTOs that carry `parentOrgCode` and/or `orgLevel`. */
export function isRootOrganisation(organisation: RootOrganisationCandidate | null | undefined): boolean {
  return !!organisation && (isRootParentOrgCode(organisation.parentOrgCode) || organisation.orgLevel === 0);
}

/** Deterministic pick if several candidates exist: lowest orgCode wins, ties keep input order. */
export function pickFirstByOrgCode<T extends { orgCode?: string | null }>(
  items: readonly T[] | null | undefined
): T | undefined {
  return [...(items ?? [])].sort((first, second) =>
    (first?.orgCode ?? '').localeCompare(second?.orgCode ?? '', 'en')
  )[0];
}

export function pickRootOrganisation<T extends RootOrganisationCandidate>(
  organisations: readonly T[] | null | undefined
): T | undefined {
  return pickFirstByOrgCode((organisations ?? []).filter(organisation => isRootOrganisation(organisation)));
}

/** Every root org code the business rules below must support ("SE" historical / possible revert, "O" current). */
export const KNOWN_ROOT_ORG_CODES = ['SE', 'O'] as const;
export type KnownRootOrgCode = (typeof KNOWN_ROOT_ORG_CODES)[number];

export function isKnownRootOrgCode(orgCode: string | null | undefined): orgCode is KnownRootOrgCode {
  return !!orgCode && (KNOWN_ROOT_ORG_CODES as readonly string[]).includes(orgCode);
}

/** A known root itself or an org below it by code: "O", "O GP" → true; "OX", "OFFSHORE A", "SEX A" → false. */
export function isInKnownRootBranch(orgCode: string | null | undefined): boolean {
  return !!orgCode && KNOWN_ROOT_ORG_CODES.some(root => orgCode === root || orgCode.startsWith(`${root} `));
}

/** "<root> <suffix>" for every known root, e.g. ['GP'] → ['SE GP', 'O GP']. */
function withKnownRootPrefixes(suffixes: readonly string[]): readonly string[] {
  return KNOWN_ROOT_ORG_CODES.flatMap(root => suffixes.map(suffix => `${root} ${suffix}`));
}

/**
 * Merit: org codes whose drill-down does NOT add a merit hierarchy level (they count on their parent's level).
 * Backend mirrors still hard-code "SE GP" and need the same known-root change: Employees OrganisationService
 * (`Contains("SE GP")`, lines 109 and 188) and Seasons OrganisationSnapshotExtensions (lines 12, 22, 32).
 * Note: `Contains("O GP")` would false-match codes such as "INFO GP".
 */
export const MERIT_LEVEL_NEUTRAL_ORG_CODES: readonly string[] = withKnownRootPrefixes(['GP']);

export function isMeritLevelNeutralOrgCode(orgCode: string | null | undefined): boolean {
  return !!orgCode && MERIT_LEVEL_NEUTRAL_ORG_CODES.includes(orgCode);
}

/**
 * Merit: org-code prefixes whose merit hierarchy level is one less than their token count suggests.
 * Backend mirrors still hard-code "SE GS…" and need the same known-root change: Importer
 * LoadPlanOrganisationsImportService (line 148) and Seasons MeritPrePublicationService IsSpecialOrgCode (line 1090).
 */
export const MERIT_LEVEL_SHIFTED_ORG_CODE_PREFIXES: readonly string[] = withKnownRootPrefixes([
  'GS',
  'GT',
  'TI',
  'FIN',
  'WP'
]);

export function hasMeritLevelShiftedOrgCodePrefix(orgCode: string | null | undefined): boolean {
  return !!orgCode && MERIT_LEVEL_SHIFTED_ORG_CODE_PREFIXES.some(prefix => orgCode.startsWith(prefix));
}

/** Org planning: positions held by SGRE employees inside organisations of a known root branch are highlighted. */
export const SGRE_EMPLOYEE_ORG_CODE_MARKER = '(SGRE';

export function isSgreEmployeeInRootBranchOrganisation(
  orgCode: string | null | undefined,
  employeeOrgCode: string | null | undefined
): boolean {
  return isInKnownRootBranch(orgCode) && !!employeeOrgCode?.includes(SGRE_EMPLOYEE_ORG_CODE_MARKER);
}
